// Shared client-config accessor.
//
// The API exposes runtime feature flags at GET /api/config/client (see
// apps/api/src/routes/health.routes.js). The web app fetches this once per
// session and shares the result — flags like `destructiveDemoResetEnabled` are
// server-driven so a deployed build can toggle demo controls without a rebuild.
//
// The fetch is cached at module scope so multiple `useClientConfig` consumers
// (topbar, Help Center, …) share a single network round-trip. A failed fetch
// degrades gracefully to an empty flag set (every gated control stays hidden)
// and is retried on the next mount.

import { useEffect, useState } from 'react';
import { api } from './apiClient.js';

const EMPTY = Object.freeze({ apiBase: '/api', actor: null, auth: null, sandbox: null, flags: {} });

let cachedValue = null;
let cachedPromise = null;

/**
 * Fetch (and memoize) the client config. Always resolves — a network/auth
 * failure yields an EMPTY config so callers never have to handle rejection.
 *
 * @returns {Promise<{ apiBase?: string, actor: object|null, flags: object }>}
 */
export function fetchClientConfig() {
  if (cachedValue) return Promise.resolve(cachedValue);
  if (!cachedPromise) {
    cachedPromise = Promise.resolve()
      .then(() => api.get('/config/client'))
      .then((payload) => {
        cachedValue = {
          ...EMPTY,
          ...(payload || {}),
          auth: payload?.auth || null,
          flags: { ...(payload?.flags || {}) },
        };
        return cachedValue;
      })
      .catch(() => {
        // Allow a later mount to retry; do not poison the cache with a value.
        cachedPromise = null;
        return EMPTY;
      });
  }
  return cachedPromise;
}

// Test-only: clear the module cache between cases.
export function __resetClientConfigCache() {
  cachedValue = null;
  cachedPromise = null;
}

/**
 * React hook exposing the shared client config.
 *
 * @returns {{ apiBase?: string, actor: object|null, flags: object, loading: boolean }}
 */
export function useClientConfig() {
  const [state, setState] = useState(() =>
    cachedValue ? { ...cachedValue, loading: false } : { ...EMPTY, loading: true },
  );
  useEffect(() => {
    let alive = true;
    fetchClientConfig().then((cfg) => {
      if (alive) setState({ ...cfg, loading: false });
    });
    return () => {
      alive = false;
    };
  }, []);
  return state;
}
